<template>
  <div class="category-page-wrapper"
       ref="scrollableContent"
       @touchstart="handleTouchStart"
       :class="{ 'prevent-interaction': pageAnimating }">
    <div class="detail-header-bar" :style="{ paddingTop: insetTop + 'px'}">
      <img src="@/tarot_box/pages/home/assets/icons/back.svg" class="back-button" @click="$emit('closeCategoryPage')"/>
      <div class="view-toggle">
        <div class="toggle-button"
             :class="{ active: viewMode === 'list' }"
             @click="viewMode = 'list'">
          <img src="@/tarot_box/pages/home/assets/icons/listbullet.svg" alt="LIST"/>
        </div>
        <div class="toggle-button"
             :class="{ active: viewMode === 'grid' }"
             @click="viewMode = 'grid'">
          <img src="@/tarot_box/pages/home/assets/icons/squares.svg" alt="GRID"/>
        </div>
      </div>
    </div>

    <!-- 필터 메뉴바 (category 또는 subject 타입일 때만 표시) -->
    <div v-if="showFilterBar"
         class="filter-bar"
         :class="{ 'header-hidden': !isHeaderVisible }"
         :style="{ top: headerBarHeight + 'px' }">
      <h2 class="Headline1">{{ categoryTitle }}</h2>
      <div class="filter-scroll">
        <button
            v-for="filter in filterOptions"
            :key="filter.value"
            class="filter-button title3"
            :class="{ active: selectedFilter === filter.value }"
            @click="selectFilter(filter.value)">
          {{ filter.label }}
        </button>
      </div>
    </div>

    <div v-else
         class="section-title-bar"
         :class="{ 'header-hidden': !isHeaderVisible }"
         :style="{ top: headerBarHeight + 'px' }">
      <h2 class="Headline1">{{ categoryTitle }}</h2>
    </div>

    <div class="content-container">
      <!-- 그리드 뷰 -->
      <div v-if="viewMode === 'grid'" class="category-grid-view">
        <div
            v-for="boxId in filteredBoxIds"
            :key="boxId"
            class="grid-item"
            @click="handleCardClick(boxId)">

          <!-- 썸네일 영역 (할인율 오버레이 포함) -->
          <div class="grid-thumbnail-container Shadow1">
            <img
                :src="getBoxById(boxId).thumbnailImgUrl"
                class="grid-thumbnail"
                :style="{
                    width: gridImageWidth + 'px',
                    height: gridImageHeight + 'px',
                    clipPath: getGridClipPath
                  }"
                alt="Thumbnail Image"/>

            <!-- 할인율 오버레이 -->
            <div v-if="getPriceInfo(boxId).hasDiscount" class="grid-discount-overlay Caption1 Gray9">
              {{ getDiscountRate(getPriceInfo(boxId).originalPrice, getPriceInfo(boxId).salePrice) }}%
            </div>
          </div>

          <!-- 가격 정보 -->
          <div class="grid-price">
            <span v-if="getPriceInfo(boxId).hasDiscount" class="grid-original-price Title2 Gray5">
              {{ formatPrice(getPriceInfo(boxId).originalPrice) }}{{ getPriceInfo(boxId).unit }}
            </span>
            <span class="grid-sale-price Caption1 Gray2">
              {{ formatPrice(getPriceInfo(boxId).salePrice) }}{{ getPriceInfo(boxId).unit }}
              <img v-if="!getPriceInfo(boxId).isWeb"
                   src="@/tarot_box/pages/box_input_v2/assets/clover.svg"
                   alt="Clover Icon"
                   style="height: 14px; width: auto;"/>
            </span>
          </div>

          <!-- 제목 -->
          <div class="grid-title Title2 Gray4">
            {{ newLine(getBoxById(boxId).title) }}
          </div>

          <div class="grid-info">
              <!-- 리뷰가 있을 때만 표시 -->
              <span v-if="getBoxById(boxId).reviewCount > 0" class="grid-info-review-count Text3">
                <img src="@/tarot_box/pages/home/assets/icons/review.svg" alt="Review Icon" style="width: 14px; height: 14px;"/>
                {{ getBoxById(boxId).reviewCount }}
              </span>
              <!-- 카테고리별 순위 표시 -->
              <span v-if="getCategoryRanking(boxId)" class="grid-info-ranking Text3">
                <img src="@/tarot_box/pages/home/assets/icons/ranking.svg" alt="Ranking Icon" style="width: 14px; height: 14px;"/>
                {{ getCategoryRanking(boxId) }}
              </span>
            </div>
        </div>
      </div>

      <!-- 리스트 뷰 -->
      <div v-else class="category-list-view">
        <div
          v-for="boxId in filteredBoxIds"
          :key="boxId"
          class="list-item"
          @click="handleCardClick(boxId)">
          <img :src="getBoxById(boxId).thumbnailImgUrl"
               class="list-thumbnail Shadow2"
               alt="Thumbnail Image"/>
          <div class="list-content">
            <div class="list-title Title2">{{ getBoxById(boxId).title }}</div>
            <div class="list-price">
              <!-- 할인율 표시 (웹/앱 공통) -->
              <span v-if="getPriceInfo(boxId).hasDiscount" class="discount-rate Caption2 Gray9">
                {{ getDiscountRate(getPriceInfo(boxId).originalPrice, getPriceInfo(boxId).salePrice) }}%
              </span>

              <!-- 원가 표시 -->
              <span v-if="getPriceInfo(boxId).hasDiscount" class="original-price Title2 Gray5">
                {{ formatPrice(getPriceInfo(boxId).originalPrice) }}{{ getPriceInfo(boxId).unit }}
              </span>

              <!-- 판매가 표시 -->
              <span class="sale-price Caption1 Gray2">
                {{ formatPrice(getPriceInfo(boxId).salePrice) }}{{ getPriceInfo(boxId).unit }}
                            <!-- 앱에서만 클로버 아이콘 표시 -->
                <img v-if="!getPriceInfo(boxId).isWeb"
                     src="@/tarot_box/pages/box_input_v2/assets/clover.svg"
                     alt="Clover Icon"
                     style="height: 14px; width: auto;"/>
              </span>
            </div>
            <div class="list-info">
              <!-- 리뷰가 있을 때만 표시 -->
              <span v-if="getBoxById(boxId).reviewCount > 0" class="list-info-review-count Text3">
                <img src="@/tarot_box/pages/home/assets/icons/review.svg" alt="Review Icon" style="width: 14px; height: 14px;"/>
                {{ getBoxById(boxId).reviewCount }}
              </span>
              <!-- 카테고리별 순위 표시 -->
              <span v-if="getCategoryRanking(boxId)" class="list-info-ranking Text3">
                <img src="@/tarot_box/pages/home/assets/icons/ranking.svg" alt="Ranking Icon" style="width: 14px; height: 14px;"/>
                {{ getCategoryRanking(boxId) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { hideBottomBar, logEvent } from "@/tarot_box/helper/native_api";
import { newLine } from "@/common/utils";

export default {
  name: "CategoryPage",
  emits: ['closeCategoryPage', 'mounted', 'before-unmount', 'request-back-handler'],
  inject: ["eventBus"],
  props: {
    category: {
      type: String,
      required: true,
    },
    pageAnimating: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters(['tarotBoxes', 'homeSections']),

    showFilterBar() {
      return this.categoryType === 'category' || this.categoryType === 'subject';
    },

    headerBarHeight() {
      return this.insetTop + 32 + 2; // insetTop + padding(16*2) + 내용 높이(약 24px)
    },

    filterOptions() {
      if (this.categoryType === 'category') {
        // category 타입일 때: 해당 카테고리 내 모든 subjectTitle들 (쉼표 분리 처리)
        const subjects = [...new Set(
            this.categoryBoxIds
                .map(id => this.getBoxById(id))
                .filter(box => box)
                .flatMap(box => box.subjectTitle ? box.subjectTitle.split(',').map(s => s.trim()) : [])
                .filter(subject => subject && subject !== '심층풀이')
        )];

        const options = [
          {
            label: '전체',
            value: 'all',
            count: this.categoryBoxIds.length
          }
        ];

        subjects.forEach(subject => {
          const count = this.categoryBoxIds.filter(id => {
            const box = this.getBoxById(id);
            return box && box.subjectTitle && box.subjectTitle.split(',').map(s => s.trim()).includes(subject);
          }).length;

          options.push({
            label: subject,
            value: subject,
            count: count
          });
        });

        return options;

      } else if (this.categoryType === 'subject') {
        // subject 타입일 때: 해당 subject를 가진 모든 category들 (쉼표 분리 처리)
        const categories = [...new Set(
            this.categoryBoxIds
                .map(id => this.getBoxById(id))
                .filter(box => box)
                .flatMap(box => box.category ? box.category.split(',').map(c => c.trim()) : [])
                .filter(category => category && category !== '심층풀이')
        )];

        const options = [
          {
            label: '전체',
            value: 'all',
            count: this.categoryBoxIds.length
          }
        ];

        categories.forEach(category => {
          const count = this.categoryBoxIds.filter(id => {
            const box = this.getBoxById(id);
            return box && box.category && box.category.split(',').map(c => c.trim()).includes(category);
          }).length;

          options.push({
            label: category,
            value: category,
            count: count
          });
        });

        return options;
      }

      return [];
    },
    filteredBoxIds() {
      if (!this.showFilterBar || this.selectedFilter === 'all') {
        return this.categoryBoxIds;
      }

      if (this.categoryType === 'category') {
        // subjectTitle로 필터링 (쉼표 분리 처리)
        return this.categoryBoxIds.filter(id => {
          const box = this.getBoxById(id);
          return box && box.subjectTitle &&
              box.subjectTitle.split(',').map(s => s.trim()).includes(this.selectedFilter);
        });
      } else if (this.categoryType === 'subject') {
        // category로 필터링 (쉼표 분리 처리)
        return this.categoryBoxIds.filter(id => {
          const box = this.getBoxById(id);
          return box && box.category &&
              box.category.split(',').map(c => c.trim()).includes(this.selectedFilter);
        });
      }

      return this.categoryBoxIds;
    },
    getGridClipPath() {
      const width = this.gridImageWidth;
      const height = this.gridImageHeight;

      // corner-smoothing 0.6을 위한 계산
      const cornerSize = Math.min(width, height) * 0.2; // 15% 정도가 적절
      const smoothingFactor = cornerSize * 0.6;

      return `path("M${smoothingFactor},0 L${width-smoothingFactor},0 C${width-smoothingFactor*0.3},0 ${width},${smoothingFactor*0.3} ${width},${smoothingFactor} L${width},${height-smoothingFactor} C${width},${height-smoothingFactor*0.3} ${width-smoothingFactor*0.3},${height} ${width-smoothingFactor},${height} L${smoothingFactor},${height} C${smoothingFactor*0.3},${height} 0,${height-smoothingFactor*0.3} 0,${height-smoothingFactor} L0,${smoothingFactor} C0,${smoothingFactor*0.3} ${smoothingFactor*0.3},0 ${smoothingFactor},0 Z")`;
    }
  },
  data() {
    const imgWidth = (window.innerWidth - (16*2) - (12*2))/3;

    // 새로운 계산 (2열, 20px 간격)
    const containerWidth = window.innerWidth - (20*2); // 좌우 패딩 20px
    const gridItemWidth = (containerWidth - 20) / 2; // 2열, 중간 간격 20px
    const gridImageWidth = gridItemWidth;
    const gridImageHeight = (gridImageWidth * 226) / 158; // 158:226 비율
    return {
      categoryTitle: '',
      categoryBoxIds: [],
      categoryType: '',
      insetTop: 16,
      viewMode: 'list', // 'grid' 또는 'list'
      selectedFilter: 'all',
      thumbnailImageWidth: imgWidth + 'px', // 기존 (삭제 예정)
      gridImageWidth: gridImageWidth,
      gridImageHeight: gridImageHeight,
      lastScrollTop: 0,
      isHeaderVisible: true,
      scrollThreshold: 50
    };
  },
  mounted() {
    this.$emit('mounted');

    hideBottomBar();
    this.addSafeAreaColorTop('--Grayscale-Gray12');
    this.addSafeAreaColorBottom('#FFFFFFFF');
    this.setCategoryData();
    this.insetTop = parseInt(new URL(window.location.href).searchParams.get('inset_top')) + 16 || 16;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    this.$nextTick(() => {
      this.addScrollListener();
    });
    if (window.onBackButtonClick === undefined || window.onBackButtonClick === null) {
      console.warn("Back button handler missing in CategoryPage, requesting from parent");
      this.$emit('request-back-handler');
    }
  },
  beforeUnmount() {
    this.$emit('before-unmount');
    this.previousSafeAreaColorBottom();
    this.previousSafeAreaColorTop();
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    this.removeScrollListener();
  },
  methods: {
    ...mapActions(['addSafeAreaColorTop', 'addSafeAreaColorBottom', 'previousSafeAreaColorTop', 'previousSafeAreaColorBottom']),

    newLine,

    initializeScroll() {
      const scrollableContent = this.$refs.scrollableContent;
      if (scrollableContent) {
        scrollableContent.style.webkitOverflowScrolling = 'touch';
        scrollableContent.style.overscrollBehavior = 'contain';
        scrollableContent.style.transform = 'translateZ(0)';
        scrollableContent.style.willChange = 'transform';

        setTimeout(() => {
          scrollableContent.scrollTop = 1;
          scrollableContent.scrollTop = 0;
        }, 0);
      }
    },

    handleTouchStart(event) {
      event.stopPropagation();
    },

    getBoxById(boxId) {
      return this.tarotBoxes.find(box => box.id === boxId) || {};
    },

    selectFilter(filterValue) {
      this.selectedFilter = filterValue;

      // 로그 이벤트
      logEvent('category_filter_click', {
        category_type: this.categoryType,
        category_name: this.categoryTitle,
        filter_value: filterValue
      });
    },

    setCategoryData() {
      if (Number.isInteger(Number(this.category))) {
        this.setCategoryDataByType('section');
        this.categoryType = 'section';
        logEvent('home_section_list_into', {'category_name': this.categoryTitle});
      } else {
        const categoryType = this.category.split('/')[0];
        if (categoryType === 'tarot_box_subject_title') {
          this.setCategoryDataByType('subject');
          this.categoryType = 'subject';
          logEvent('home_subject_list_into', {'category_name': this.categoryTitle});
        } else if (categoryType === 'tarot_box_category') {
          this.categoryType = 'category';
          this.setCategoryDataByType('category');
          logEvent('home_category_list_into', {'category_name': this.categoryTitle});
        }
      }
    },
    setCategoryDataByType(categoryType) {
      if (categoryType === 'category') {
        this.categoryTitle = this.category.split('/')[1];
        this.categoryBoxIds = this.tarotBoxes
            .filter(box => {
              if (!box.category) return false;
              const categories = box.category.split(',').map(c => c.trim());
              return categories.includes(this.categoryTitle) && !categories.includes('심층풀이');
            })
            .sort((a, b) => b.categoryPriority - a.categoryPriority)
            .map(box => box.id);
      } else if (categoryType === 'subject') {
        this.categoryTitle = this.category.split('/')[1];
        this.categoryBoxIds = this.tarotBoxes
            .filter(box => {
              if (!box.subjectTitle) return false;
              const subjects = box.subjectTitle.split(',').map(s => s.trim());
              return subjects.some(subject => subject.includes(this.categoryTitle)) &&
                  !subjects.includes('심층풀이');
            })
            .sort((a, b) => b.categoryPriority - a.categoryPriority)
            .map(box => box.id);
      } else if (categoryType === 'section') {
        const categoryData = this.homeSections.find(section => section.id === Number(this.category));
        this.categoryTitle = categoryData.title;
        // 섹션에서도 심층풀이 제외
        this.categoryBoxIds = categoryData.tarotBoxIds.filter(boxId => {
          const box = this.getBoxById(boxId);
          if (!box) return true;
          const categories = box.category ? box.category.split(',').map(c => c.trim()) : [];
          const subjects = box.subjectTitle ? box.subjectTitle.split(',').map(s => s.trim()) : [];
          return !categories.includes('심층풀이') && !subjects.includes('심층풀이');
        });
      }
    },
    handleCardClick(boxId) {
      this.eventBus.emit('openTarotDetail', boxId);
      this.logClickEvent(boxId);
    },

    logClickEvent(boxId) {
      const box = this.tarotBoxes.find(box => box.id === boxId);
      const boxTitle = box ? box.title : '';

      logEvent('home_contents_click', {
        "tarot_box_id": boxId,
        "tarot_box_name": boxTitle,
        "click_origin_name": this.categoryTitle,
        "click_origin": this.categoryType + '_list',
        "view_mode": this.viewMode,
        "selected_filter": this.selectedFilter
      });

      logEvent('home_' + this.categoryType + '_list_contents_click', {
        "tarot_box_id": boxId,
        "tarot_box_name": boxTitle,
        "view_mode": this.viewMode,
        "selected_filter": this.selectedFilter
      });
    },

    onBackButtonClick() {
      this.$emit('closeCategoryPage');
    },
    formatPrice(price) {
      return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    // 할인율 계산 함수
    getDiscountRate(originalPrice, salePrice) {
      if (!originalPrice || !salePrice || originalPrice <= salePrice) return 0;
      return Math.round(((originalPrice - salePrice) / originalPrice) * 100);
    },

    // 가격 정보 가져오기 (웹/앱 구분)
    getPriceInfo(boxId) {
      const box = this.getBoxById(boxId);
      const isWeb = this.$isWeb();

      const originalPrice = isWeb ? box.originalPriceWon : box.originalPrice;
      const salePrice = isWeb ? box.salePriceWon : box.salePrice;
      const unit = isWeb ? '원' : '';

      return {
        originalPrice,
        salePrice,
        unit,
        isWeb,
        hasDiscount: originalPrice && salePrice && originalPrice !== salePrice
      };
    },

    // 카테고리별 순위 계산 메서드
    getCategoryRanking(boxId) {
      const box = this.getBoxById(boxId);
      if (!box || (box.rankingScore === null || box.rankingScore === undefined)) return null;

      const overallRanking = this.calculateOverallRanking(boxId);
      if (overallRanking && overallRanking <= 9) {
        return `종합 ${overallRanking}위`;
      }

      // 2. 카테고리별 순위 확인 (종합 9위 밖인 경우)
      const categoryPriority = ['솔로탈출', '재회', '직장취업', '자녀운'];
      
      // 해당 타로박스가 속한 모든 카테고리 찾기
      const boxCategories = [];
      
      // subjectTitle에서 카테고리 찾기
      if (box.subjectTitle) {
        const subjects = box.subjectTitle.split(',').map(s => s.trim());
        boxCategories.push(...subjects);
      }
      
      // category에서도 찾기 (중복 제거)
      if (box.category) {
        const categories = box.category.split(',').map(c => c.trim());
        categories.forEach(cat => {
          if (!boxCategories.includes(cat)) {
            boxCategories.push(cat);
          }
        });
      }

      // 우선순위에 따라 카테고리별 순위 계산
      for (const priorityCategory of categoryPriority) {
        if (boxCategories.includes(priorityCategory)) {
          const ranking = this.calculateRankingInCategory(boxId, priorityCategory);
          if (ranking !== null && ranking <= 9) { // 상위 9위 이내만 표시 (0도 포함)
            return `${priorityCategory} ${ranking}위`;
          }
        }
      }

      return null;
    },

    // 전체 타로박스 중 순위 계산 (종합 순위)
    calculateOverallRanking(boxId) {
      const box = this.getBoxById(boxId);
      if (!box) return null;

      // 모든 타로박스를 rankingScore 기준으로 정렬
      const allBoxes = [...this.tarotBoxes].sort((a, b) => b.rankingScore - a.rankingScore);
      
      // 현재 타로박스의 순위 찾기
      const ranking = allBoxes.findIndex(item => item.id === boxId) + 1;
      
      return ranking > 0 ? ranking : null;
    },

    // 특정 카테고리에서의 순위 계산
    calculateRankingInCategory(boxId, category) {
      const box = this.getBoxById(boxId);
      if (!box) {
        return null;
      }

      // 해당 카테고리에 속하는 모든 타로박스 필터링
      let categoryBoxes = this.tarotBoxes.filter(item => {
        // subjectTitle에서 카테고리 찾기
        if (item.subjectTitle) {
          const subjects = item.subjectTitle.split(',').map(s => s.trim());
          if (subjects.includes(category)) {
            return true;
          }
        }
        
        // category에서도 찾기
        if (item.category) {
          const categories = item.category.split(',').map(c => c.trim());
          if (categories.includes(category)) {
            return true;
          }
        }
        
        return false;
      });

      // rankingScore 기준으로 정렬
      categoryBoxes.sort((a, b) => b.rankingScore - a.rankingScore);
      
      // 현재 타로박스의 순위 찾기
      const ranking = categoryBoxes.findIndex(item => item.id === boxId) + 1;
      
      // findIndex가 -1을 반환하면 찾지 못한 것이므로 null 반환
      return ranking > 0 ? ranking : null;
    },
    addScrollListener() {
      const scrollableContent = this.$refs.scrollableContent;
      if (scrollableContent) {
        scrollableContent.addEventListener('scroll', this.handleScroll, { passive: true });
      }
    },

    // 스크롤 이벤트 핸들러
    handleScroll() {
      const scrollableContent = this.$refs.scrollableContent;
      if (!scrollableContent) return;

      const currentScrollTop = scrollableContent.scrollTop;
      const scrollDiff = Math.abs(currentScrollTop - this.lastScrollTop);

      // threshold 이상 스크롤했을 때만 반응
      if (scrollDiff < this.scrollThreshold) return;

      // 스크롤 방향 감지
      const isScrollingDown = currentScrollTop > this.lastScrollTop;
      const isAtTop = currentScrollTop <= this.scrollThreshold;

      // 헤더 표시/숨김 결정
      if (isAtTop) {
        // 맨 위에 있으면 항상 표시
        this.isHeaderVisible = true;
      } else if (isScrollingDown && this.isHeaderVisible) {
        // 아래로 스크롤하면 숨김
        this.isHeaderVisible = false;
      } else if (!isScrollingDown && !this.isHeaderVisible) {
        // 위로 스크롤하면 표시
        this.isHeaderVisible = true;
      }

      this.lastScrollTop = currentScrollTop;
    },

    // 스크롤 이벤트 리스너 제거
    removeScrollListener() {
      const scrollableContent = this.$refs.scrollableContent;
      if (scrollableContent) {
        scrollableContent.removeEventListener('scroll', this.handleScroll);
      }
    }
  }
}
</script>

<style scoped>
.prevent-interaction {
  pointer-events: none;
}

.category-page-wrapper {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--Grayscale-Gray12);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9996;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: none;
}

.detail-header-bar {
  top: 0;
  position: sticky;
  width: 100%;
  padding: 10px 20px;
  max-width: 480px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  background-color: white;
  z-index: 900;
}

.back-button {
  z-index: 9999;
  cursor: pointer;
}

h2 {
  margin: 0;
}

.view-toggle {
  display: flex;
  gap: 12px;
}

.toggle-button {
  width: 24px;
  height: 24px;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.toggle-button img{
  opacity: 0.4;
}

.toggle-button:hover {
  background-color: var(--Grayscale-Gray11);
}

.toggle-button.active {
  background-color: var(--Grayscale-Gray10);
}

.toggle-button.active img {
  opacity: 1;
}

.filter-bar,
.section-title-bar {
  position: sticky;
  background-color: white;
  z-index: 800;
  padding: 12px 20px;
  transition: transform 0.3s ease-in-out;
  transform: translateY(0);
}

.filter-bar.header-hidden,
.section-title-bar.header-hidden {
  transform: translateY(-100%);
}

/* 부드러운 전환을 위한 추가 스타일 */
.filter-bar {
  padding: 12px 0;
}

.filter-bar h2 {
  text-align: left;
  padding-left: 20px;
}

.filter-scroll {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 14px 16px 0 16px;
  -webkit-overflow-scrolling: touch;
}

.filter-scroll::-webkit-scrollbar {
  display: none;
}

.section-title-bar h2 {
  text-align: left;
  margin: 0;
}

.filter-button {
  flex-shrink: 0;
  padding: 8px 16px;
  border-radius: 20px;
  background-color: var(--Gray8);
  color: var(--Gray5);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
}

.filter-button.active {
  background-color: var(--Gray2);
  color: var(--Gray9);
}

.filter-count {
  font-size: 12px;
  opacity: 0.7;
}

.content-container {
  flex-grow: 1;
  padding: 20px 20px;
  box-sizing: border-box;
}


/* 새로운 그리드 뷰 스타일 */
.category-grid-view {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px 20px; /* 세로 32px, 가로 20px */
  padding: 0;
}

.grid-item {
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: transform 0.2s;
}

.grid-item:hover {
  transform: translateY(-2px);
}

.grid-thumbnail-container {
  position: relative;
  margin-bottom: 8px;
}

.grid-thumbnail {
  width: 100%;
  object-fit: cover;
  display: block;
}

/* 할인율 오버레이 (스타일링은 사용자가 직접) */
.grid-discount-overlay {
  position: absolute;
  bottom: 0;
  right: 0;
  background-color: var(--Alert-Color);
  padding: 6px 8px;
  border-radius: 16px 0 12px 0;
}

.grid-price {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  gap: 2px;
}

.grid-price span {
  display: flex;
  align-items: center;
}

.grid-original-price {
  text-decoration: line-through;
}

.grid-sale-price {
  line-height: 17px !important;
  display: flex;
  align-items: center;
  gap: 2px;
}

.grid-title {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: left;
  word-break: keep-all;
  line-height: 1.4;
}

.grid-info{
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
}

.grid-info-review-count{
  display: flex;
  align-items: center;
  gap: 3.33px;
  color: #00497A;
  background-color: #EFF8FF;
  border-radius: 4px;
  padding: 2px 4px;
}

.grid-info-ranking{
  display: flex;
  align-items: center;
  gap: 2px;
  color: #E05C15;
  background-color: #FFF3EC;
  border-radius: 4px;
  padding: 2px 4px;
}


/* 리스트 뷰 스타일 */
.category-list-view {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.list-item {
  display: flex;
  gap: 16px;
  background-color: white;
  cursor: pointer;
}

.list-thumbnail {
  flex-shrink: 0;
  width: 84px;
  height: 117px;
  overflow: hidden;
  clip-path: path("M20,0 L64,0 C70,0 75,0.8 78.5,3.2 C82,5.6 83.2,9.5 84,15 L84,102 C83.2,107.5 82,111.4 78.5,113.8 C75,116.2 70,117 64,117 L20,117 C14,117 9,116.2 5.5,113.8 C2,111.4 0.8,107.5 0,102 L0,15 C0.8,9.5 2,5.6 5.5,3.2 C9,0.8 14,0 20,0 Z");
  border-radius: 16px;
}

.list-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 12px;
}

.list-title {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: left;
  word-break: keep-all;
}

.discount-rate{
  background-color: var(--AlertColor);
  padding: 2px 5px;
  border-radius: 10px;
}

.list-price {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.list-price span{
  display: flex;
  align-items: center;
  gap: 2px;
}

.list-info{
  display: flex;
  align-items: center;
  gap: 4px;
}

.list-info-review-count{
  display: flex;
  align-items: center;
  gap: 3.33px;
  color: #00497A;
  background-color: #EFF8FF;
  border-radius: 4px;
  padding: 2px 4px;
}

.list-info-ranking{
  display: flex;
  align-items: center;
  gap: 2px;
  color: #E05C15;
  background-color: #FFF3EC;
  border-radius: 4px;
  padding: 2px 4px;

}

.original-price {
  text-decoration: line-through;
}

.sale-price {
  line-height: 17px !important;
}
</style>